import { useCallback, useEffect, useState } from 'react';

import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';

import { useGetYardsQuery } from '@/app/services/lookups';
import { SELECTION_LIMIT } from '@/constants';
import { Yard } from '@/rtk-query.gql';

import './YardsAutoComplete.css';

type YardsAutoCompleteProps = AutoCompleteProps & {
  className?: string;
  label?: string;
  defaultValue?: Yard;
  defaultValueId?: string;
  appendTo?: string | null;
};

export const YardsAutoComplete = ({
  className,
  label,
  defaultValue,
  defaultValueId,
  appendTo = 'self',
  onSelect: externalOnSelect,
  onChange: externalOnChange,
  ...autoCompleteProps
}: YardsAutoCompleteProps) => {
  const [selected, setSelected] = useState<Yard | undefined>();
  const [inputValue, setInputValue] = useState('');
  const [filtered, setFiltered] = useState<Yard[]>([]);

  const { data: yardData } = useGetYardsQuery({});

  // Reverse-lookup: populate display value from a stored ID
  useEffect(() => {
    if (defaultValueId && yardData) {
      const yard = yardData.find((y) => y.id === defaultValueId);
      if (yard) {
        setSelected(yard);
        setInputValue(yard.name ?? '');
      }
    }
  }, [defaultValueId, yardData]);

  // Controlled defaultValue object
  useEffect(() => {
    if (defaultValue) {
      setSelected(defaultValue);
      setInputValue(defaultValue.name ?? '');
    }
  }, [defaultValue]);

  const search = useCallback(
    (event: { query: string }) => {
      const searchTerm = event.query.toLowerCase();
      const filteredYards =
        yardData
          ?.filter(
            (yard) =>
              yard?.name?.toLowerCase().includes(searchTerm) ||
              yard?.yard?.toLowerCase().includes(searchTerm),
          )
          .slice(0, SELECTION_LIMIT) || [];

      setFiltered(filteredYards);
    },
    [yardData],
  );

  const itemTemplate = (yard: Yard) => {
    return `${yard.name}`;
  };

  const handleSelect = (e: { value: Yard }) => {
    setSelected(e.value);
    setInputValue(e.value?.name as string);
    externalOnSelect?.(e as any);
  };

  const handleChange = (e: { value: string | Yard }) => {
    const value =
      typeof e.value === 'object' && e.value !== null
        ? e.value.name
        : (e.value as string);
    setInputValue(value as string);
    externalOnChange?.(e as any);
  };

  return (
    <div
      className={`[ Yards-auto-complete ] [${className ? ` ${className}` : ''} label-input-group ]`}
    >
      {label && <label>{label}</label>}
      <AutoComplete
        appendTo={appendTo}
        suggestions={filtered}
        selectionLimit={SELECTION_LIMIT}
        completeMethod={search}
        onSelect={handleSelect}
        onChange={handleChange}
        itemTemplate={itemTemplate}
        dropdown={true}
        {...autoCompleteProps}
        value={inputValue}
        className={className}
      />
    </div>
  );
};
